import React from 'react';
import { X, User, Mail, Phone, Calendar, Car, CreditCard, CheckCircle, XCircle, MapPin } from 'lucide-react';

const ClientDetailsModal = ({ client, onClose }) => {
  if (!client) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const activeSubscriptions = client.clientSubscriptions?.filter(s => new Date(s.end_date) > new Date()) || [];
  const inactiveSubscriptions = client.clientSubscriptions?.filter(s => new Date(s.end_date) <= new Date()) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full mx-4 mb-8">
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                <User size={24} />
              </div>
              Client Details
            </h2>
            <p className="text-blue-100 text-sm mt-1">Complete client information and history</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Client Information */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <div className="bg-blue-600 p-2 rounded-lg">
                <User size={20} className="text-white" />
              </div>
              Client Information / معلومات العميل
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-white rounded-lg p-4">
                <User className="text-blue-600 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Name / الاسم</p>
                  <p className="text-lg font-semibold text-gray-800">{client.username}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white rounded-lg p-4">
                <Mail className="text-green-600 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Email / البريد الإلكتروني</p>
                  <p className="text-lg font-semibold text-gray-800 break-all">{client.email}</p>
                  {client.is_email_validated ? (
                    <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <CheckCircle size={12} /> Verified
                    </span>
                  ) : (
                    <span className="text-xs text-red-600 flex items-center gap-1 mt-1">
                      <XCircle size={12} /> Not Verified
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white rounded-lg p-4">
                <Phone className="text-purple-600 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Mobile / الجوال</p>
                  <p className="text-lg font-semibold text-gray-800">{client.mobile}</p>
                  {client.is_mobile_validated ? (
                    <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <CheckCircle size={12} /> Verified
                    </span>
                  ) : (
                    <span className="text-xs text-red-600 flex items-center gap-1 mt-1">
                      <XCircle size={12} /> Not Verified
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white rounded-lg p-4">
                <Calendar className="text-orange-600 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Member Since / عضو منذ</p>
                  <p className="text-lg font-semibold text-gray-800">{formatDate(client.date_created)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white rounded-lg p-4">
                <CreditCard className="text-indigo-600 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Client ID / رقم العميل</p>
                  <p className="text-lg font-semibold text-gray-800 font-mono">{client.client_id}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white rounded-lg p-4">
                <CheckCircle className="text-teal-600 mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Status / الحالة</p>
                  <div className="flex gap-2 mt-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      client.subscription_status === 'Active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {client.subscription_status}
                    </span>
                    {client.free_wash_status && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                        Free Wash
                      </span>
                    )}
                    {client.is_profile_complete && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        Complete Profile
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicles Section */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <div className="bg-purple-600 p-2 rounded-lg">
                <Car size={20} className="text-white" />
              </div>
              Registered Vehicles ({client.vehicle?.length || 0}) / المركبات المسجلة
            </h3>
            {client.vehicle && client.vehicle.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {client.vehicle.map((vehicle) => (
                  <div key={vehicle.vehicle_id} className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-lg text-gray-800">{vehicle.model}</h4>
                        <p className="text-sm text-gray-600">Year: {vehicle.year_made}</p>
                      </div>
                      <div 
                        className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-sm"
                        style={{ backgroundColor: vehicle.color }}
                        title={vehicle.color}
                      ></div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Plate Number:</span>
                        <span className="font-semibold font-mono">{vehicle.plate_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Registration:</span>
                        <span className="font-semibold">{vehicle.registration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Color Code:</span>
                        <span className="font-mono text-xs">{vehicle.color}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicle ID:</span>
                        <span className="font-mono text-xs">{vehicle.vehicle_id}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg p-8 text-center text-gray-500">
                <Car size={48} className="mx-auto mb-2 text-gray-400" />
                <p>No vehicles registered</p>
              </div>
            )}
          </div>

          {/* Active Subscriptions */}
          {activeSubscriptions.length > 0 && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="bg-green-600 p-2 rounded-lg">
                  <CheckCircle size={20} className="text-white" />
                </div>
                Active Subscriptions ({activeSubscriptions.length}) / الاشتراكات النشطة
              </h3>
              <div className="space-y-4">
                {activeSubscriptions.map((sub) => (
                  <div key={sub.client_subscription_id} className="bg-white rounded-lg p-5 border border-green-200">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-bold text-lg text-gray-800">{sub.subscription_name}</h4>
                        <p className="text-sm text-gray-600">
                          {sub.subscription?.description_en || 'Premium subscription plan'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">QAR {sub.subscription_cost}</p>
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                          {sub.payment_status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                      <div>
                        <p className="text-gray-600">Start Date</p>
                        <p className="font-semibold">{formatDate(sub.start_date)}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">End Date</p>
                        <p className="font-semibold">{formatDate(sub.end_date)}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Created</p>
                        <p className="font-semibold">{formatDate(sub.created_date)}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Subscription ID</p>
                        <p className="font-mono text-xs">{sub.client_subscription_id}</p>
                      </div>
                    </div>

                    {/* Subscribed Vehicles */}
                    {sub.subscribedVehicles && sub.subscribedVehicles.length > 0 && (
                      <div className="border-t pt-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          Subscribed Vehicles ({sub.subscribedVehicles.length}):
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {sub.subscribedVehicles.map((sv) => (
                            <div 
                              key={sv.subscribed_vehicle_id}
                              className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm"
                            >
                              <p className="font-semibold">{sv.vehicle.model}</p>
                              <p className="text-xs text-gray-600 font-mono">{sv.vehicle.plate_number}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inactive/Past Subscriptions */}
          {inactiveSubscriptions.length > 0 && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="bg-gray-600 p-2 rounded-lg">
                  <Calendar size={20} className="text-white" />
                </div>
                Past Subscriptions ({inactiveSubscriptions.length}) / الاشتراكات السابقة
              </h3>
              <div className="space-y-3">
                {inactiveSubscriptions.map((sub) => (
                  <div key={sub.client_subscription_id} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-gray-800">{sub.subscription_name}</h4>
                        <p className="text-xs text-gray-600">
                          {formatDate(sub.start_date)} - {formatDate(sub.end_date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-600">QAR {sub.subscription_cost}</p>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          Expired
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Subscriptions Message */}
          {!client.clientSubscriptions || client.clientSubscriptions.length === 0 && (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <Calendar size={48} className="mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600">No subscription history available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 rounded-b-lg flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientDetailsModal;